import { Badge, type BadgeColor, Tooltip } from '@civilgrid/ui/components';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { type DeployInfo, fetchDeployInfo } from '~/api/deployInfo';
import getConfig from '~/config';

type PreviewEnvBadgeColor = Extract<BadgeColor, 'grey' | 'blue' | 'green' | 'orange'>;

type PreviewEnvBadgeProps = {
  actor: string;
  reason: string;
  deployedAt?: string;
  detail?: string;
  color?: PreviewEnvBadgeColor;
  'data-testid'?: string;
};

const BADGE_COLORS: Record<string, PreviewEnvBadgeColor> = {
  silver: 'grey',
  blue: 'blue',
  green: 'green',
  orange: 'orange',
};

/** Every preview environment shows the badge, including a staging build serving a preview slot. */
export function shouldShowPreviewEnvBadge(env: string, previewEnv?: string): boolean {
  return Boolean(previewEnv) || env.startsWith('preview');
}

/** Maps the workflow's team_color choice onto a badge color; anything unrecognized reads as grey. */
export function toBadgeColor(color: string): PreviewEnvBadgeColor {
  return BADGE_COLORS[color.trim().toLowerCase()] ?? 'grey';
}

/** Formats the deploy timestamp in the viewer's timezone; undefined when the api sent nothing usable. */
export function formatDeployedAt(deployedAt: string): string | undefined {
  if (!deployedAt) return undefined;

  const parsed = DateTime.fromISO(deployedAt);
  return parsed.isValid ? parsed.toLocal().toFormat('LLL d, h:mm a') : undefined;
}

/** Maps a `/tick` deploy record onto the badge's props. */
export function previewEnvBadgeProps(deploy: DeployInfo): PreviewEnvBadgeProps {
  return {
    actor: deploy.actor,
    reason: deploy.reason,
    deployedAt: formatDeployedAt(deploy.deployedAt),
    color: toBadgeColor(deploy.color),
    detail: deploy.gitsha,
  };
}

/** Team-colored pill naming who owns a preview environment and why. */
export function PreviewEnvBadgeContent({ actor, reason, deployedAt, detail, color, ...props }: PreviewEnvBadgeProps) {
  const tooltipText = [actor && `Deployed by @${actor}`, reason, deployedAt && `Last deployed: ${deployedAt}`, detail]
    .filter(Boolean)
    .join(' · ');

  return (
    <Tooltip text={tooltipText}>
      {/* Capped width because `reason` is unbounded free text and the tooltip trigger wraps this badge. */}
      <Badge
        kind="outline"
        size="small"
        color={color ?? 'grey'}
        className="max-w-xs flex-col items-stretch justify-center gap-0"
        {...props}
      >
        <span className="flex items-baseline gap-1.5">
          {actor && <span className="min-w-0 truncate font-semibold">@{actor}</span>}
          {deployedAt && <span className="shrink-0 text-grey-70">{deployedAt}</span>}
        </span>
        {reason && <span className="truncate">{reason}</span>}
      </Badge>
    </Tooltip>
  );
}

/** Names the person who deployed the preview env the viewer is on, and why. */
export default function TopNavPreviewEnvBadge() {
  const { env, previewEnv } = getConfig();
  const isPreview = shouldShowPreviewEnvBadge(env, previewEnv);
  const [deploy, setDeploy] = useState<DeployInfo | null>(null);

  useEffect(() => {
    if (!isPreview) return;

    let cancelled = false;
    const load = () => {
      fetchDeployInfo().then((info) => {
        if (!cancelled) setDeploy(info);
      });
    };

    load();

    // A preview env can be redeployed under a viewer who left the tab open, so re-read the owner
    // whenever they come back to it — a stale name is worse than none.
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isPreview]);

  if (!isPreview || !deploy) return null;

  return <PreviewEnvBadgeContent {...previewEnvBadgeProps(deploy)} data-testid="top-nav-deploy-badge" />;
}
